/**
 * Server-side data fetching utilities for City pages
 * This file uses EXISTING GraphQL functions from the modules
 *
 * Strategy:
 * 1. JSON first (SSR/SEO/fast initial render)
 * 2. Then GraphQL (real-time updates on client if needed)
 *
 * This file is for SERVER-SIDE and CLIENT-SIDE data fetching
 * Following the same pattern as Neighborhood serverDataFetcher
 */

// Import existing GraphQL functions
import {
  getcitydata,
  getAllquestions,
  getsimilarcities,
} from "../../../modules/Graphql/cityquestionandanswergraphql.js";
import {
  getAllcityreview,
  getcityscore,
} from "../../../modules/Graphql/cityreviewgraphql.js";
import {
  getcityimages,
} from "../../../modules/Graphql/cityimagesgraphql.js";
import { getschoolincity } from "../../../modules/Graphql/schoolincitygraphql.js";

/**
 * Fetch city data details from GraphQL API
 * Uses the existing getcitydata function
 * @param {string} cityfullname - The city full name (slug)
 * @returns {Promise<Object>} City data
 */
export async function fetchCityDetails(cityfullname) {
  try {
    const getdata = {
      start: 0,
      limit: 100,
      order: "DESC",
      orderBy: "npi",
      fields: [{ name: "cityfullname", value: cityfullname, operator: "=" }],
    };

    const result = await getcitydata(getdata);
    return result;
  } catch (error) {
    console.error("Error fetching city details:", error);
    return { data: { getcitydata: [] } };
  }
}

/**
 * Fetch city reviews
 * Uses the existing getAllcityreview function
 */
export async function fetchCityReviews(cityname, statename) {
  try {
    const getdata = {
      start: 0,
      limit: 100,
      orderBy: "helpfulCount",
      order: "DESC",
      fields: [
        { name: "cityname", value: cityname, operator: "=" },
        { name: "statename", value: statename, operator: "=" },
      ],
    };

    const result = await getAllcityreview(getdata);
    return result;
  } catch (error) {
    console.error("Error fetching city reviews:", error);
    return { data: { getcityreview: [] } };
  }
}

/**
 * Fetch city score data
 * Uses the existing getcityscore function
 */
export async function fetchCityScore(cityname, statename) {
  try {
    const getdata = {
      start: 0,
      limit: 1,
      orderBy: "id",
      order: "DESC",
      fields: [
        { name: "cityname", value: cityname, operator: "=" },
        { name: "statename", value: statename, operator: "=" },
      ],
    };

    const result = await getcityscore(getdata);
    return result;
  } catch (error) {
    console.error("Error fetching city score:", error);
    return { data: { getcityscore: [] } };
  }
}

/**
 * Fetch city images
 * Uses the existing getcityimages function
 */
export async function fetchCityImages(cityname, statename) {
  try {
    const getdata = {
      start: 0,
      limit: 100,
      orderBy: "id",
      order: "DESC",
      fields: [
        { name: "cityname", value: cityname, operator: "=" },
        { name: "statename", value: statename, operator: "=" },
      ],
    };

    const result = await getcityimages(getdata);
    return result;
  } catch (error) {
    console.error("Error fetching city images:", error);
    return { data: { getcityimage: [] } };
  }
}

/**
 * Fetch questions for a city
 * Uses the existing getAllquestions function
 */
export async function fetchCityQuestions(cityId) {
  try {
    const getdata = {
      start: 0,
      limit: 100,
      orderBy: "updatedDate",
      order: "DESC",
      fields: [{ name: "cityId", value: cityId, operator: "=" }],
    };

    const result = await getAllquestions(getdata);
    return result;
  } catch (error) {
    console.error("Error fetching city questions:", error);
    return { data: { getcityquestions: [] } };
  }
}

/**
 * Fetch school data for a city
 * Uses the existing getschoolincity function
 */
export async function fetchSchoolInCity(cityname, statename) {
  try {
    const getdata = {
      start: 0,
      limit: 100,
      orderBy: "instructionlevel",
      order: "DESC",
      fields: [
        { name: "city", value: cityname, operator: "=" },
        { name: "state", value: statename, operator: "=" },
      ],
    };

    const result = await getschoolincity(getdata);
    return result;
  } catch (error) {
    console.error("Error fetching schools in city:", error);
    return { data: { getschoolincity: [] } };
  }
}

/**
 * Fetch similar cities
 * Uses the existing getsimilarcities function
 */
export async function fetchSimilarCities(cityfullname) {
  try {
    const getdata = {
      start: 0,
      limit: 10,
      orderBy: "id",
      order: "DESC",
      fields: [{ name: "cityfullname", value: cityfullname, operator: "=" }],
    };

    const result = await getsimilarcities(getdata);
    return result;
  } catch (error) {
    console.error("Error fetching similar cities:", error);
    return { data: { getsimilarcities: [] } };
  }
}

/**
 * Fetch all data for a city page (main aggregator function)
 * This is the MAIN function used by CityDataHydration.js
 */
export async function fetchAllCityData(cityfullname) {
  try {
    console.log(`🔄 Fetching GraphQL data for city: ${cityfullname}`);

    // First, fetch the main city details
    const cityDetailsData = await fetchCityDetails(cityfullname);

    if (
      !cityDetailsData ||
      !cityDetailsData.data ||
      !cityDetailsData.data.getcitydata ||
      cityDetailsData.data.getcitydata.length === 0
    ) {
      console.log(`❌ No city data found for: ${cityfullname}`);
      return null;
    }

    const cityDetails = cityDetailsData?.data?.getcitydata?.[0];
    const cityId = cityDetails?.id;
    const cityName = cityDetails?.postalcity_name;
    const stateName = cityDetails?.state_name;

    // Fetch all related data in parallel for better performance
    const [
      reviewsData,
      scoresData,
      imagesData,
      questionsData,
      schoolsData,
      similarCitiesData,
    ] = await Promise.all([
      fetchCityReviews(cityName, stateName),
      fetchCityScore(cityName, stateName),
      fetchCityImages(cityName, stateName),
      fetchCityQuestions(cityId),
      fetchSchoolInCity(cityName, stateName),
      fetchSimilarCities(cityfullname),
    ]);

    console.log(`✅ All GraphQL data fetched successfully for ${cityfullname}`);

    // Combine all data into a single result object
    // This matches the structure expected by CityComponent
    const result = {
      data: {
        getcitydata: cityDetailsData?.data?.getcitydata || [],
        getcityreview: reviewsData?.data?.getcityreview || [],
        getcityscore: scoresData?.data?.getcityscore || [],
        getcityimage: imagesData?.data?.getcityimage || [],
        getcityquestions: questionsData?.data?.getcityquestions || [],
        getschoolincity: schoolsData?.data?.getschoolincity || [],
        getsimilarcities: similarCitiesData?.data?.getsimilarcities || [],
      },
    };

    return { result };
  } catch (error) {
    console.error("❌ Error fetching all city data:", error);
    return null;
  }
}
